import { convertVideoFrameToImageData } from './utils/misc';

function isMobileSafari(): boolean {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent;
    return /iPhone|iPad|iPod/i.test(ua) && /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua);
}

function isIPadDevice(): boolean {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent;
    if (/iPad/i.test(ua)) return true;
    return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

const DEFAULT_IPAD_FRONT_CAMERA_ZOOM = 1.5;

function normalizeCameraLabel(label: string): string {
    return label.trim().toLowerCase();
}

function isLikelyBackCameraLabel(label: string): boolean {
    const normalized = normalizeCameraLabel(label);
    return /back|rear|environment|world/.test(normalized);
}

function isLikelyFrontCameraLabel(label: string): boolean {
    const normalized = normalizeCameraLabel(label);
    if (!normalized) return false;
    if (isLikelyBackCameraLabel(normalized)) return false;
    return /front|user|facetime|true.?depth|selfie/.test(normalized);
}

function scoreIPadFrontCameraLabel(label: string): number {
    const normalized = normalizeCameraLabel(label);
    let score = 0;

    if (isLikelyFrontCameraLabel(normalized)) score += 100;
    if (/true.?depth/.test(normalized)) score += 40;
    if (/front|user|facetime/.test(normalized)) score += 20;
    if (/wide|ultra/.test(normalized)) score -= 80;
    if (/back|rear|environment|desk|virtual/.test(normalized)) score -= 200;

    return score;
}

export default class WebcamClient {
    private videoElement: HTMLVideoElement;
    private stream?: MediaStream;
    private frameCallback?: (frame: ImageData, timestamp: number) => Promise<void>;
    private isStarted: boolean = false;
    private isProcessingFrames: boolean = false;
    private captureCanvas?: HTMLCanvasElement;
    private captureCtx?: CanvasRenderingContext2D | null;
    private targetFrameIntervalMs: number = 1000 / 24;
    private lastProcessedAt: number = 0;
    private captureWidth?: number;
    private captureHeight?: number;
    private currentDeviceId?: string;

    constructor(videoElementId: string) {
        const videoElement = document.getElementById(videoElementId) as HTMLVideoElement;
        if (!videoElement) {
            throw new Error(`Video element with id '${videoElementId}' not found`);
        }
        this.videoElement = videoElement;
    }

    private getVideoTrack(): MediaStreamTrack | undefined {
        return this.stream?.getVideoTracks?.()[0];
    }

    private buildConstraints(deviceId?: string): MediaStreamConstraints {
        const mobileSafari = isMobileSafari();
        this.targetFrameIntervalMs = mobileSafari ? 1000 / 8 : 1000 / 24;
        this.captureWidth = mobileSafari ? 320 : undefined;
        this.captureHeight = mobileSafari ? 240 : undefined;

        return {
            video: {
                width: { ideal: mobileSafari ? 320 : 640 },
                height: { ideal: mobileSafari ? 240 : 480 },
                frameRate: mobileSafari ? { ideal: 8, max: 10 } : { ideal: 24, max: 30 },
                ...(deviceId ? { deviceId: { exact: deviceId } } : { facingMode: "user" })
            },
            audio: false
        };
    }

    private async attachStream(stream: MediaStream): Promise<void> {
        this.stream = stream;
        const track = this.getVideoTrack();
        const settings = track?.getSettings?.();
        this.currentDeviceId = settings?.deviceId;

        await this.applyIPadFrontCameraZoomIfAvailable();

        this.videoElement.srcObject = this.stream;
        this.videoElement.muted = true;
        this.videoElement.setAttribute('muted', '');
        this.videoElement.setAttribute('playsinline', '');
        this.videoElement.setAttribute('webkit-playsinline', 'true');

        await this.videoElement.play().catch((error) => {
            console.warn('[WebcamClient] video.play() failed after attachStream', error);
        });
    }

    private async autoSelectLastDetectedCamera(): Promise<void> {
        if (!isIPadDevice()) return;

        const devices = await this.listVideoDevices();
        if (devices.length <= 1) return;

        const lastDevice = devices[devices.length - 1];
        if (!lastDevice?.deviceId) return;

        const currentDeviceId = this.getCurrentDeviceId();
        if (lastDevice.deviceId === currentDeviceId) return;

        const currentDevice = devices.find((device) => device.deviceId === currentDeviceId);
        console.info('[WebcamClient] switching to last detected camera', {
            from: currentDevice?.label || currentDeviceId || 'unknown',
            to: lastDevice.label || lastDevice.deviceId,
        });
        await this.switchCamera(lastDevice.deviceId);
    }

    async startWebcam(frameCallback?: (frame: ImageData, timestamp: number) => Promise<void>): Promise<void> {
        if (this.isStarted) {
            console.log('[WebcamClient] startWebcam called again, updating frame callback only');
            if (frameCallback) {
                this.frameCallback = frameCallback;
            }
            return;
        }

        console.log('[WebcamClient] startWebcam begin');
        try {
            if (!navigator.mediaDevices?.getUserMedia) {
                throw new Error('This browser does not support camera access (getUserMedia).');
            }

            const constraints = this.buildConstraints();

            // Request webcam access
            console.log('[WebcamClient] requesting getUserMedia', constraints);
            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            console.log('[WebcamClient] getUserMedia success');
            await this.attachStream(stream);
            await this.autoSelectLastDetectedCamera();
            // Set the callback if provided
            if (frameCallback) {
                this.frameCallback = frameCallback;
            }

            // Start video playback
            this.videoElement.onloadedmetadata = async () => {
                console.log('[WebcamClient] onloadedmetadata', {
                    width: this.videoElement.videoWidth,
                    height: this.videoElement.videoHeight,
                });
                await this.videoElement.play();
                console.log('[WebcamClient] video.play() resolved');
            };

            this.videoElement.addEventListener('loadeddata', () => {
                console.log('[WebcamClient] loadeddata event, starting frame loop');
                this._processFrames();
            });

            this.videoElement.addEventListener('error', () => {
                console.error('[WebcamClient] video element error');
            });
            this.videoElement.addEventListener('stalled', () => {
                console.warn('[WebcamClient] video stalled');
            });

            this.isStarted = true;
            // `loadeddata` can fire before the listener is attached; ensure loop always starts.
            this._processFrames();
            console.log('[WebcamClient] startWebcam complete');

        } catch (error) {
            console.error("Error accessing the webcam:", error);
            throw error;
        }
    }

    getZoomInfo(): {
        supported: boolean;
        min: number;
        max: number;
        step: number;
        current: number;
    } | null {
        const track = this.getVideoTrack();
        if (!track) return null;

        const capabilities = track.getCapabilities?.() as MediaTrackCapabilities & {
            zoom?: { min: number; max: number; step: number };
        };
        const settings = track.getSettings?.() as MediaTrackSettings & {
            zoom?: number;
        };

        if (!capabilities?.zoom) {
            return {
                supported: false,
                min: 1,
                max: 1,
                step: 0.1,
                current: settings?.zoom ?? 1,
            };
        }

        return {
            supported: true,
            min: capabilities.zoom.min,
            max: capabilities.zoom.max,
            step: capabilities.zoom.step || 0.1,
            current: settings?.zoom ?? capabilities.zoom.min,
        };
    }

    async listVideoDevices(): Promise<MediaDeviceInfo[]> {
        if (!navigator.mediaDevices?.enumerateDevices) return [];
        const devices = await navigator.mediaDevices.enumerateDevices();
        return devices.filter((device) => device.kind === 'videoinput');
    }

    getCurrentDeviceId(): string | undefined {
        return this.currentDeviceId ?? this.getVideoTrack()?.getSettings?.().deviceId;
    }

    async switchCamera(deviceId: string): Promise<boolean> {
        if (!navigator.mediaDevices?.getUserMedia) return false;
        if (!deviceId) return false;

        try {
            const nextStream = await navigator.mediaDevices.getUserMedia(this.buildConstraints(deviceId));
            this.stream?.getTracks().forEach((track) => track.stop());
            await this.attachStream(nextStream);
            return true;
        } catch (error) {
            console.warn('[WebcamClient] failed to switch camera', error);
            return false;
        }
    }

    async setZoom(zoom: number): Promise<boolean> {
        const track = this.getVideoTrack();
        if (!track) return false;

        const info = this.getZoomInfo();
        if (!info?.supported) return false;

        const clamped = Math.min(info.max, Math.max(info.min, zoom));

        try {
            await track.applyConstraints({
                advanced: [{ zoom: clamped } as MediaTrackConstraintSet],
            });
            return true;
        } catch (advancedError) {
            try {
                await track.applyConstraints({ zoom: clamped } as MediaTrackConstraints);
                return true;
            } catch (error) {
                console.warn('[WebcamClient] failed to apply zoom constraint', error ?? advancedError);
                return false;
            }
        }
    }

    private async applyIPadFrontCameraZoomIfAvailable(): Promise<void> {
        if (!isIPadDevice()) return;

        const track = this.getVideoTrack();
        if (!track) return;

        const settings = track.getSettings?.();
        if (settings?.facingMode && settings.facingMode !== 'user') return;

        const zoomInfo = this.getZoomInfo();
        if (!zoomInfo?.supported) {
            console.info('[WebcamClient] iPad detected but camera zoom is not supported on this browser/device');
            return;
        }

        const targetZoom = Math.min(
            zoomInfo.max,
            Math.max(zoomInfo.min, DEFAULT_IPAD_FRONT_CAMERA_ZOOM)
        );

        if (targetZoom <= zoomInfo.current + 0.001) return;

        const applied = await this.setZoom(targetZoom);
        if (applied) {
            console.info('[WebcamClient] applied iPad front camera zoom', { targetZoom });
        }
    }

    stopWebcam(): void {
        if (this.stream) {
            this.stream.getTracks().forEach(track => track.stop());
            this.stream = undefined;
        }
        this.isStarted = false;
        this.isProcessingFrames = false;
        console.log('[WebcamClient] webcam stopped');
    }

    private _processFrames(): void {
        if (this.isProcessingFrames) return;
        this.isProcessingFrames = true;

        const process = async () => {
            if (!this.isProcessingFrames) return;

            if (!this.videoElement || this.videoElement.paused || this.videoElement.ended) {
                requestAnimationFrame(process);
                return;
            }

            const now = performance.now();
            if (now - this.lastProcessedAt < this.targetFrameIntervalMs) {
                requestAnimationFrame(process);
                return;
            }

            this.lastProcessedAt = now;

            // Convert the current video frame to ImageData
            this.captureCanvas ??= document.createElement('canvas');
            this.captureCtx ??= this.captureCanvas.getContext('2d');
            const imageData = convertVideoFrameToImageData(
                this.videoElement,
                this.captureCanvas,
                this.captureCtx,
                this.captureWidth,
                this.captureHeight
            );

            // Call the frame callback if provided
            if (this.frameCallback) {
                try {
                    await this.frameCallback(imageData, this.videoElement.currentTime);
                } catch (error) {
                    console.error('[WebcamClient] frame callback failed', error);
                }
            }

            // Request the next frame
            requestAnimationFrame(process);
        };

        requestAnimationFrame(process);
    }
}
